import { distanceColor } from './clue';
import { distanceKm, type Country, type Lang } from './countries';

const LABEL: Record<string, string> = { classic: 'Classic', flag: 'Flag', outline: 'Outline' };

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function globe(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  ctx.strokeStyle = '#3a2c16';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx, cy, r * 0.42, r, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - r, cy);
  ctx.lineTo(cx + r, cy);
  ctx.stroke();
  ctx.fillStyle = '#b5462f';
  ctx.beginPath();
  ctx.moveTo(cx, cy - r - 4);
  ctx.lineTo(cx + 7, cy);
  ctx.lineTo(cx - 7, cy);
  ctx.fill();
}

/** 生成 1200×630 复古羊皮纸分享图,返回 PNG dataURL */
export async function makeShareImage(opts: {
  mode: string;
  num: number;
  guesses: Country[];
  answer: Country;
  streak: number;
  lang: Lang;
  endless?: boolean;
  best?: number;
}): Promise<string> {
  const { mode, num, guesses, answer, streak, endless = false, best = 0 } = opts;
  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  try {
    await document.fonts.ready;
  } catch {
    /* ignore */
  }

  ctx.fillStyle = '#e9dcb8';
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = '#b08a4e';
  ctx.lineWidth = 3;
  ctx.strokeRect(22, 22, W - 44, H - 44);
  ctx.strokeStyle = '#c9ac74';
  ctx.lineWidth = 1;
  ctx.strokeRect(32, 32, W - 64, H - 64);

  globe(ctx, 1040, 150, 70);

  // 字标
  ctx.textBaseline = 'alphabetic';
  ctx.font = "700 92px Cinzel, Georgia, serif";
  ctx.fillStyle = '#2e2412';
  ctx.fillText('GEO', 80, 172);
  const gw = ctx.measureText('GEO').width;
  ctx.fillStyle = '#b5462f';
  ctx.fillText('DLE', 80 + gw, 172);

  // 模式 + 期号 + 成绩
  ctx.fillStyle = '#4a3a1c';
  ctx.font = 'italic 34px Spectral, Georgia, serif';
  ctx.fillText(
    endless ? `${LABEL[mode] ?? mode}  ·  Unlimited` : `${LABEL[mode] ?? mode}  ·  #${num}  ·  ${guesses.length}/∞`,
    82,
    224,
  );

  // 结果方块(按距离连续取色,命中=墨绿)
  const n = Math.max(guesses.length, 1);
  const sq = Math.max(28, Math.min(66, Math.floor((W - 200) / n) - 12));
  const gap = 12;
  const totalW = guesses.length * sq + (guesses.length - 1) * gap;
  let x = (W - totalW) / 2;
  const y = 300;
  for (const g of guesses) {
    const hit = g.cca3 === answer.cca3;
    ctx.fillStyle = hit ? '#4f7a3f' : distanceColor(distanceKm(g, answer));
    roundRect(ctx, x, y, sq, sq, 8);
    ctx.fill();
    x += sq + gap;
  }

  ctx.textAlign = 'center';
  ctx.fillStyle = '#2e2412';
  ctx.font = '600 32px Spectral, Georgia, serif';
  ctx.fillText(
    endless
      ? `Solved in ${guesses.length}  ·  Streak ${streak}  ·  Best ${best}`
      : `Solved in ${guesses.length}  ·  Streak ${streak}`,
    W / 2,
    470,
  );
  ctx.fillStyle = '#6b4f2a';
  ctx.font = 'italic 28px Spectral, Georgia, serif';
  ctx.fillText('geodle.org', W / 2, 516);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/png');
}

const MODE_LABEL: Record<string, string> = { classic: 'Classic', flag: 'Flag', outline: 'Outline' };
const ORDER = ['classic', 'flag', 'outline'];

/** 今日整体战报图(不剧透:各模式完成态 + 连胜) */
export async function makeReportImage(opts: {
  num: number;
  solved: string[];
  streak: number;
  lang: Lang;
}): Promise<string> {
  const { num, solved, streak } = opts;
  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  try {
    await document.fonts.ready;
  } catch {
    /* ignore */
  }

  ctx.fillStyle = '#e9dcb8';
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = '#b08a4e';
  ctx.lineWidth = 3;
  ctx.strokeRect(22, 22, W - 44, H - 44);
  ctx.strokeStyle = '#c9ac74';
  ctx.lineWidth = 1;
  ctx.strokeRect(32, 32, W - 64, H - 64);

  globe(ctx, 1040, 130, 60);

  ctx.textAlign = 'center';
  ctx.font = "700 80px Cinzel, Georgia, serif";
  ctx.fillStyle = '#2e2412';
  ctx.fillText('GEODLE', W / 2, 150);
  ctx.fillStyle = '#4a3a1c';
  ctx.font = 'italic 34px Spectral, Georgia, serif';
  // 只统计三个核心模式(Connections / Draw 不计入战报)
  const coreSolved = solved.filter((m) => ORDER.includes(m)).length;
  ctx.fillText(`#${num}  ·  Today ${coreSolved}/${ORDER.length}`, W / 2, 205);

  // 三个模式牌(已完成=墨绿+勾,未完成=暗置)
  const tw = 300;
  const th = 150;
  const gap = 28;
  const totalW = ORDER.length * tw + (ORDER.length - 1) * gap;
  let x = (W - totalW) / 2;
  const y = 270;
  for (const m of ORDER) {
    const done = solved.includes(m);
    ctx.fillStyle = done ? '#4f7a3f' : 'rgba(60,40,15,0.07)';
    roundRect(ctx, x, y, tw, th, 14);
    ctx.fill();
    if (!done) {
      ctx.strokeStyle = '#b08a4e';
      ctx.lineWidth = 1.5;
      roundRect(ctx, x, y, tw, th, 14);
      ctx.stroke();
    }
    ctx.fillStyle = done ? '#f6edd6' : '#7a6440';
    ctx.font = "600 30px Cinzel, Georgia, serif";
    ctx.fillText(MODE_LABEL[m], x + tw / 2, y + th / 2 - 6);
    if (done) {
      // 勾
      ctx.strokeStyle = '#f6edd6';
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      const cx = x + tw / 2;
      const cy = y + th / 2 + 30;
      ctx.beginPath();
      ctx.moveTo(cx - 16, cy);
      ctx.lineTo(cx - 4, cy + 12);
      ctx.lineTo(cx + 18, cy - 14);
      ctx.stroke();
    }
    x += tw + gap;
  }

  ctx.fillStyle = '#2e2412';
  ctx.font = '600 32px Spectral, Georgia, serif';
  ctx.fillText(`Streak ${streak}`, W / 2, 500);
  ctx.fillStyle = '#6b4f2a';
  ctx.font = 'italic 28px Spectral, Georgia, serif';
  ctx.fillText('geodle.org', W / 2, 542);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/png');
}

const CONN_TIER_COLOR = ['', '#c79a3e', '#4f7a3f', '#3e6068', '#9e3b27']; // tier 1..4

/** Connections 分享图:复古卡 + 提交序列的难度色方块网格。 */
export async function makeConnectionsImage(opts: {
  num: number;
  submissions: string[][]; // picks(cca3)
  tierOf: (cca3: string) => number;
  won: boolean;
}): Promise<string> {
  const { num, submissions, tierOf, won } = opts;
  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  try {
    await document.fonts.ready;
  } catch {
    /* ignore */
  }

  ctx.fillStyle = '#e9dcb8';
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = '#b08a4e';
  ctx.lineWidth = 3;
  ctx.strokeRect(22, 22, W - 44, H - 44);
  ctx.strokeStyle = '#c9ac74';
  ctx.lineWidth = 1;
  ctx.strokeRect(32, 32, W - 64, H - 64);
  globe(ctx, 1040, 150, 70);

  ctx.textBaseline = 'alphabetic';
  ctx.font = '700 92px Cinzel, Georgia, serif';
  ctx.fillStyle = '#2e2412';
  ctx.fillText('GEO', 80, 172);
  const gw = ctx.measureText('GEO').width;
  ctx.fillStyle = '#b5462f';
  ctx.fillText('DLE', 80 + gw, 172);

  ctx.fillStyle = '#4a3a1c';
  ctx.font = 'italic 34px Spectral, Georgia, serif';
  ctx.fillText(`Connections  ·  #${num}  ·  ${won ? 'Solved' : 'X'}`, 82, 224);

  // 方块网格:按行数自适应缩放,居中落在标题与水印之间的色带内,绝不溢出。
  const rows = Math.max(submissions.length, 1);
  const gap = 12;
  const bandTop = 250;
  const bandBottom = H - 96; // 给底部水印留白
  const availH = bandBottom - bandTop;
  const availW = W - 200;
  const cell = Math.max(
    20,
    Math.min(84, Math.floor((availW - 3 * gap) / 4), Math.floor((availH - (rows - 1) * gap) / rows)),
  );
  const gridW = 4 * cell + 3 * gap;
  const gridH = rows * cell + (rows - 1) * gap;
  const x0 = (W - gridW) / 2;
  const y0 = bandTop + (availH - gridH) / 2;
  const r = Math.min(12, Math.round(cell * 0.18));
  submissions.forEach((row, ri) => {
    row.forEach((cca3, c) => {
      ctx.fillStyle = CONN_TIER_COLOR[tierOf(cca3)] || '#cbbd95';
      roundRect(ctx, x0 + c * (cell + gap), y0 + ri * (cell + gap), cell, cell, r);
      ctx.fill();
    });
  });

  ctx.fillStyle = '#7a6033';
  ctx.font = '600 30px Cinzel, Georgia, serif';
  ctx.textAlign = 'center';
  ctx.fillText('geodle.org', W / 2, H - 56);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/png');
}
